/**
 * AGC: Purchase History Component for Origami Battle 2.0
 * AGC: Shows all marketplace purchases with filters and transaction links
 * @author AGC (AI-Generated Code) with Cursor
 */

import React, { useState, useEffect, useMemo } from 'react'
import { useAccount } from 'wagmi'
import { formatUnits, parseAbi } from 'viem'
import { ArrowLeft, ExternalLink, Calendar, Filter, ShoppingBag } from 'lucide-react'
import type { ActivePage } from './MainMenu'
import { buildBackendUrl } from '../utils/backendUrl'

// AGC: Contract addresses from environment
const BUFF1155_ADDRESS = (import.meta.env.VITE_BUFF1155_ADDRESS || '0x0000000000000000000000000000000000000000') as `0x${string}`
const CLASSIC_POTION1155_ADDRESS = (import.meta.env.VITE_CLASSIC_POTION1155_ADDRESS || '0x0000000000000000000000000000000000000000') as `0x${string}`
const BASE_EXPLORER_URL = import.meta.env.VITE_BASE_EXPLORER_URL || 'https://sepolia.basescan.org'

// AGC: Contract ABIs for events
const BUFF1155_ABI = parseAbi([
  'event BuffPurchased(address indexed buyer, uint256 indexed tokenId, uint256 amount, string paymentMethod, uint256 price)',
])

const CLASSIC_POTION1155_ABI = parseAbi([
  'event PotionPurchased(address indexed buyer, uint256 indexed tokenId, uint256 potionTypeId, uint8 rarity, string paymentMethod, uint256 price)',
])

// AGC: Buff metadata
const BUFF_NAMES: { [key: number]: string } = {
  0: 'Shield Buff',
  1: 'Pierce Buff',
  2: 'Haste Buff',
  3: 'Frost Buff',
}

// AGC: Potion type names
const POTION_TYPE_NAMES: { [key: number]: string } = {
  0: 'Healing Potion',
  1: 'Mana Potion',
  2: 'Double Strike Potion',
  3: 'Poison Potion',
}

// AGC: Rarity names
const RARITY_NAMES: { [key: number]: string } = {
  0: 'Common',
  1: 'Uncommon',
  2: 'Rare',
  3: 'Epic',
  4: 'Legendary',
}

interface PurchaseRecord {
  type: 'buff' | 'potion'
  itemName: string
  tokenId: bigint
  amount: bigint
  price: bigint
  paymentMethod: string
  txHash: string
  blockNumber: bigint
  timestamp: number
  rarity?: number
  potionTypeId?: number
}

// AGC: FIX 2025-11-28 - Asset quotes for converting ETH/OGB to USDC equivalent
interface AssetQuotes {
  ogbPerUsdMicro: bigint
  ethPerUsdMicro: bigint
  usdcPerUsdMicro: bigint
  updatedAt: number
}

type FilterPeriod = 'today' | 'yesterday' | 'last7days' | 'last30days' | 'thisMonth' | 'lastMonth' | 'total'

interface PurchaseHistoryProps {
  onNavigateToPage?: (page: ActivePage) => void
}

export const PurchaseHistory: React.FC<PurchaseHistoryProps> = ({ onNavigateToPage }) => {
  const { address, isConnected } = useAccount()
  const [purchases, setPurchases] = useState<PurchaseRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filterPeriod, setFilterPeriod] = useState<FilterPeriod>('total')
  const [viewMode, setViewMode] = useState<'mine' | 'all'>('mine') // AGC: FIX 2025-11-28 - Toggle between personal and all users
  const [assetQuotes, setAssetQuotes] = useState<AssetQuotes | null>(null) // AGC: FIX 2025-11-28 - Asset quotes for USD conversion

  // AGC: FIX 2025-11-28 - Fallback asset quotes for testnet (when contract quotes are 0)
  // AGC: Based on: 728 OGB = $0.01 → 72800 OGB per USD → 72800e18 wei per USD
  // AGC: ETH at ~$3333/ETH → 1e18/3333 ≈ 300000000000000 wei per USD
  const FALLBACK_OGB_PER_USD_MICRO = 72800000000000000000000n // 72800e18 - based on 728 OGB = $0.01
  const FALLBACK_ETH_PER_USD_MICRO = 300000000000000n // ~$3333/ETH for testnet estimation

  // AGC: FIX 2025-11-28 - Fetch asset quotes for ETH/OGB to USDC conversion
  useEffect(() => {
    const fetchAssetQuotes = async () => {
      try {
        const response = await fetch(buildBackendUrl('api/purchase-history/asset-quotes'))
        if (response.ok) {
          const data = await response.json()
          const ogbQuote = BigInt(data.ogbPerUsdMicro || '0')
          const ethQuote = BigInt(data.ethPerUsdMicro || '0')
          
          // AGC: Use fallback values if contract quotes are 0 (common on testnet)
          const finalOgbQuote = ogbQuote > 0n ? ogbQuote : FALLBACK_OGB_PER_USD_MICRO
          const finalEthQuote = ethQuote > 0n ? ethQuote : FALLBACK_ETH_PER_USD_MICRO
          
          if (ogbQuote === 0n || ethQuote === 0n) {
            console.log('⚠️ [PurchaseHistory] Contract quotes are 0, using fallback values:', {
              ogbQuote: ogbQuote.toString(),
              ethQuote: ethQuote.toString(),
              fallbackOgb: finalOgbQuote.toString(),
              fallbackEth: finalEthQuote.toString()
            })
          }
          
          setAssetQuotes({
            ogbPerUsdMicro: finalOgbQuote,
            ethPerUsdMicro: finalEthQuote,
            usdcPerUsdMicro: BigInt(data.usdcPerUsdMicro || '1000000'),
            updatedAt: data.updatedAt || Date.now()
          })
          console.log('✅ [PurchaseHistory] Asset quotes loaded:', {
            ogb: finalOgbQuote.toString(),
            eth: finalEthQuote.toString()
          })
        } else {
          // AGC: Use fallback quotes if API fails
          console.warn('⚠️ [PurchaseHistory] API failed, using fallback quotes')
          setAssetQuotes({
            ogbPerUsdMicro: FALLBACK_OGB_PER_USD_MICRO,
            ethPerUsdMicro: FALLBACK_ETH_PER_USD_MICRO,
            usdcPerUsdMicro: 1000000n,
            updatedAt: Date.now()
          })
        }
      } catch (err) {
        console.warn('⚠️ [PurchaseHistory] Failed to fetch asset quotes, using fallback:', err)
        // AGC: Use fallback quotes on error
        setAssetQuotes({
          ogbPerUsdMicro: FALLBACK_OGB_PER_USD_MICRO,
          ethPerUsdMicro: FALLBACK_ETH_PER_USD_MICRO,
          usdcPerUsdMicro: 1000000n,
          updatedAt: Date.now()
        })
      }
    }
    fetchAssetQuotes()
  }, [])

  // AGC: Fetch purchase history from backend (Firebase cache + incremental blockchain sync)
  // AGC: FIX 2025-11-28 - Support both "My Purchases" and "All Users" views
  useEffect(() => {
    if (!isConnected || !address) {
      setPurchases([])
      return
    }

    const fetchPurchases = async () => {
      setLoading(true)
      setError(null)

      try {
        let rawPurchases: any[] = []
        let data: any = {}

        if (viewMode === 'mine') {
          // AGC: Trigger global purchase sync when viewing personal purchases (forceSync=true)
          // AGC: This ensures global cache is up-to-date when user views purchase history
          try {
            await fetch(buildBackendUrl(`api/purchase-history/revenue/aggregate?fromTimestamp=0&forceSync=true`), {
              method: 'GET',
              headers: { 'Content-Type': 'application/json' }
            })
            console.log('✅ [PurchaseHistory] Triggered global purchase sync')
          } catch (syncError) {
            console.warn('⚠️ [PurchaseHistory] Global sync failed (non-critical):', syncError)
          }

          // AGC: Call backend API which handles Firebase cache + incremental blockchain sync
          const response = await fetch(buildBackendUrl(`api/purchase-history/${address}`), {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          })

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`)
          }

          data = await response.json()
          rawPurchases = data.purchases || []
        } else {
          // AGC: View all users' purchases (global revenue data)
          // AGC: Calculate last Monday 00:00 UTC timestamp
          const now = Math.floor(Date.now() / 1000)
          const WEEK_EPOCH = 1704067200 // Monday, Jan 1, 2024 00:00:00 UTC
          const daysSinceEpoch = Math.floor((now - WEEK_EPOCH) / (24 * 60 * 60))
          const daysIntoWeek = daysSinceEpoch % 7
          const lastMonday = now - (daysIntoWeek * 24 * 60 * 60)
          const fromTimestamp = lastMonday * 1000 // Convert to milliseconds

          const response = await fetch(buildBackendUrl(`api/purchase-history/revenue/aggregate?fromTimestamp=${fromTimestamp}&useCache=true`), {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          })

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`)
          }

          data = await response.json()
          rawPurchases = data.purchases || []
        }

        // AGC: Transform backend purchase format to frontend format
        const allPurchases: PurchaseRecord[] = rawPurchases.map((p: any) => {
          // AGC: Revenue aggregate endpoint returns simplified format (no tokenId, contractType, etc.)
          if (viewMode === 'all') {
            // AGC: For "all users" view, create simplified purchase record
            let price: bigint
            if (typeof p.price === 'string') {
              price = BigInt(p.price)
            } else if (typeof p.price === 'number') {
              price = BigInt(Math.floor(p.price))
            } else if (typeof p.price === 'bigint') {
              price = p.price
            } else {
              price = 0n
            }

            const paymentMethod = p.paymentMethod || 'Unknown'
            
            return {
              type: 'potion', // AGC: Default type for aggregate data
              itemName: `${paymentMethod} Purchase`, // AGC: Show payment method in item name
              tokenId: 0n,
              amount: 1n,
              price,
              paymentMethod,
              txHash: '', // AGC: Aggregate data doesn't have txHash
              blockNumber: 0n,
              timestamp: p.timestamp || Date.now(),
            }
          } else {
            // AGC: Personal purchases have full details
            const tokenId = BigInt(p.tokenId || 0)
            const isBuff = p.contractType === 'Buff1155'
            
            let itemName = p.itemName || 'Unknown Item'
            if (isBuff) {
              const buffId = Number(tokenId)
              itemName = BUFF_NAMES[buffId] || `Buff #${buffId}`
            } else if (p.rarityName && p.potionTypeId !== undefined) {
              const potionTypeId = Number(p.potionTypeId)
              itemName = `${POTION_TYPE_NAMES[potionTypeId] || `Potion #${potionTypeId}`} (${p.rarityName || 'Unknown'})`
            }

            // AGC: FIX 2025-11-28 - Safely convert price to BigInt (handle string, number, or BigInt)
            let price: bigint
            if (typeof p.price === 'string') {
              price = BigInt(p.price)
            } else if (typeof p.price === 'number') {
              price = BigInt(Math.floor(p.price))
            } else if (typeof p.price === 'bigint') {
              price = p.price
            } else {
              price = 0n
            }

            return {
              type: isBuff ? 'buff' : 'potion',
              itemName,
              tokenId,
              amount: BigInt(p.amount || 1),
              price,
              paymentMethod: p.paymentMethod || 'Unknown',
              txHash: p.txHash,
              blockNumber: BigInt(p.blockNumber || 0),
              timestamp: p.timestamp || Date.now(),
              rarity: p.rarity,
              potionTypeId: p.potionTypeId ? Number(p.potionTypeId) : undefined,
            }
          }
        })

        // AGC: Sort by timestamp (newest first) - backend should already do this, but ensure it
        allPurchases.sort((a, b) => b.timestamp - a.timestamp)

        console.log(`✅ [PurchaseHistory] Loaded ${allPurchases.length} purchases (${viewMode === 'mine' ? 'personal' : 'all users'}, synced at ${new Date(data.syncedAt || Date.now()).toISOString()})`)
        setPurchases(allPurchases)
      } catch (err: any) {
        console.error('❌ Error fetching purchase history:', err)
        setError(err.message || 'Failed to fetch purchase history')
      } finally {
        setLoading(false)
      }
    }

    fetchPurchases()
  }, [isConnected, address, viewMode])

  // AGC: Filter purchases by period
  const filteredPurchases = useMemo(() => {
    if (filterPeriod === 'total') {
      return purchases
    }

    const now = Date.now()
    let startTime = 0

    switch (filterPeriod) {
      case 'today':
        startTime = new Date().setHours(0, 0, 0, 0)
        break
      case 'yesterday':
        const yesterday = new Date()
        yesterday.setDate(yesterday.getDate() - 1)
        startTime = yesterday.setHours(0, 0, 0, 0)
        const yesterdayEnd = new Date()
        yesterdayEnd.setDate(yesterdayEnd.getDate() - 1)
        const endTime = yesterdayEnd.setHours(23, 59, 59, 999)
        return purchases.filter((p) => p.timestamp >= startTime && p.timestamp <= endTime)
      case 'last7days':
        startTime = now - 7 * 24 * 60 * 60 * 1000
        break
      case 'last30days':
        startTime = now - 30 * 24 * 60 * 60 * 1000
        break
      case 'thisMonth':
        startTime = new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime()
        break
      case 'lastMonth':
        const lastMonth = new Date()
        lastMonth.setMonth(lastMonth.getMonth() - 1)
        startTime = new Date(lastMonth.getFullYear(), lastMonth.getMonth(), 1).getTime()
        const lastMonthEnd = new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0, 23, 59, 59, 999).getTime()
        return purchases.filter((p) => p.timestamp >= startTime && p.timestamp <= lastMonthEnd)
    }

    return purchases.filter((p) => p.timestamp >= startTime)
  }, [purchases, filterPeriod])

  // AGC: FIX 2025-11-28 - Convert price to USDC equivalent using asset quotes with fallbacks
  // AGC: Uses high-precision intermediate calculation to avoid truncation for small amounts
  const convertToUsdc = (price: bigint, paymentMethod: string): bigint => {
    const method = (paymentMethod || 'USDC').toLowerCase().trim()
    
    if (method === 'usdc') {
      // AGC: USDC is already in USDC (6 decimals)
      return price
    }
    
    // AGC: Use loaded quotes if available, otherwise use fallbacks
    const ogbQuote = assetQuotes?.ogbPerUsdMicro || FALLBACK_OGB_PER_USD_MICRO
    const ethQuote = assetQuotes?.ethPerUsdMicro || FALLBACK_ETH_PER_USD_MICRO
    
    if (method === 'eth') {
      // AGC: Convert ETH (wei) to USDC with high precision to avoid truncation
      // AGC: price * 1e18 / ethQuote gives USD with 18 decimals, then scale to 6
      const highPrecisionUsdc = (price * 1_000_000_000_000_000_000n) / ethQuote
      const result = highPrecisionUsdc / 1_000_000_000_000n // Scale back to 6 decimals
      console.log(`   [convertToUsdc] ETH: ${price} → ${result} USDC (quote: ${ethQuote})`)
      return result
    }
    
    if (method === 'ogb') {
      // AGC: Convert OGB (wei) to USDC with high precision to avoid truncation
      const highPrecisionUsdc = (price * 1_000_000_000_000_000_000n) / ogbQuote
      const result = highPrecisionUsdc / 1_000_000_000_000n // Scale back to 6 decimals
      console.log(`   [convertToUsdc] OGB: ${price} → ${result} USDC (quote: ${ogbQuote})`)
      return result
    }
    
    console.warn(`   [convertToUsdc] Unknown payment method: "${paymentMethod}"`)
    return 0n // AGC: Unknown payment method
  }

  // AGC: FIX 2025-11-28 - Calculate TOTAL spend in USDC by converting ETH/OGB to USDC equivalent
  const totals = useMemo(() => {
    console.log(`📊 [PurchaseHistory] Calculating totals for ${filteredPurchases.length} purchases`)
    console.log(`   Asset quotes loaded: ${assetQuotes ? 'YES' : 'NO (using fallbacks)'}`)
    
    const totalsByMethod: { [method: string]: { count: number; totalPrice: bigint; usdcEquivalent: bigint } } = {}
    let totalUsdcEquivalent = 0n
    let usdcCount = 0, ethCount = 0, ogbCount = 0, otherCount = 0
    
    filteredPurchases.forEach((p) => {
      const method = (p.paymentMethod || 'Unknown').trim()
      const methodLower = method.toLowerCase()
      
      if (!totalsByMethod[method]) {
        totalsByMethod[method] = { count: 0, totalPrice: 0n, usdcEquivalent: 0n }
      }
      totalsByMethod[method].count += Number(p.amount)
      totalsByMethod[method].totalPrice += p.price
      
      // AGC: Convert to USDC equivalent for unified total
      const usdcEquiv = convertToUsdc(p.price, method)
      totalsByMethod[method].usdcEquivalent += usdcEquiv
      totalUsdcEquivalent += usdcEquiv
      
      // AGC: Count by payment method
      if (methodLower === 'usdc') usdcCount++
      else if (methodLower === 'eth') ethCount++
      else if (methodLower === 'ogb') ogbCount++
      else otherCount++
    })

    console.log(`📊 [PurchaseHistory] RESULT:`)
    console.log(`   USDC: ${usdcCount}, ETH: ${ethCount}, OGB: ${ogbCount}, Other: ${otherCount}`)
    console.log(`   Total USDC equivalent: ${totalUsdcEquivalent}`)
    console.log(`   By method:`, Object.fromEntries(
      Object.entries(totalsByMethod).map(([k, v]) => [k, { count: v.count, usdc: v.usdcEquivalent.toString() }])
    ))

    return {
      count: filteredPurchases.reduce((sum, p) => sum + Number(p.amount), 0),
      totalUsdcEquivalent, // AGC: Combined total in USDC
      totalsByMethod // AGC: Keep per-method totals for display
    }
  }, [filteredPurchases, assetQuotes])

  // AGC: FIX 2025-11-28 - Format very small numbers in compact notation
  // AGC: e.g., 0.0000001 → "0.(6)1" meaning 6 zeros after decimal then 1
  const formatSmallNumber = (num: number): string => {
    if (num === 0) return '0'
    if (num >= 0.001) return parseFloat(num.toFixed(6)).toString()
    
    // AGC: Count leading zeros after decimal point
    const str = num.toFixed(18)
    const [, decimal] = str.split('.')
    if (!decimal) return num.toString()
    
    let leadingZeros = 0
    for (let i = 0; i < decimal.length; i++) {
      if (decimal[i] === '0') leadingZeros++
      else break
    }
    
    if (leadingZeros >= 3) {
      // AGC: Use compact notation: 0.(n)xxx where n is number of zeros
      const significantPart = decimal.slice(leadingZeros).replace(/0+$/, '').slice(0, 4)
      return `0.(${leadingZeros})${significantPart || '0'}`
    }
    
    return parseFloat(num.toFixed(6)).toString()
  }

  // AGC: Format per-item price (price / amount)
  // AGC: FIX 2025-11-28 - Helper to calculate and format per-item price
  const formatPricePerItem = (totalPrice: bigint, amount: bigint, paymentMethod: string) => {
    if (amount === 0n) {
      return formatPrice(totalPrice, paymentMethod)
    }
    const decimals = paymentMethod.toLowerCase() === 'usdc' ? 6 : 18
    const pricePerItem = Number(totalPrice) / Number(amount)
    const pricePerItemFormatted = pricePerItem / (10 ** decimals)
    return `${formatSmallNumber(pricePerItemFormatted)} ${paymentMethod.toUpperCase()}`
  }

  // AGC: Format price based on payment method (3 decimals max, remove trailing zeros)
  // AGC: FIX 2025-11-28 - Ensure price is always a BigInt and format correctly
  // AGC: Uses compact notation for very small numbers (e.g., 0.(6)1)
  const formatPrice = (price: bigint | string | number, paymentMethod: string) => {
    // AGC: Convert to BigInt if needed
    let priceBigInt: bigint
    if (typeof price === 'string') {
      try {
        priceBigInt = BigInt(price)
      } catch {
        priceBigInt = 0n
      }
    } else if (typeof price === 'number') {
      priceBigInt = BigInt(Math.floor(price))
    } else if (typeof price === 'bigint') {
      priceBigInt = price
    } else {
      priceBigInt = 0n
    }

    const method = (paymentMethod || 'USDC').toLowerCase()
    
    if (method === 'usdc') {
      const num = Number(formatUnits(priceBigInt, 6))
      return `${formatSmallNumber(num)} USDC`
    } else if (method === 'ogb') {
      const num = Number(formatUnits(priceBigInt, 18))
      return `${formatSmallNumber(num)} OGB`
    } else if (method === 'eth') {
      const num = Number(formatUnits(priceBigInt, 18))
      return `${formatSmallNumber(num)} ETH`
    }
    // AGC: Fallback for unknown payment methods - format as string with decimals
    return `${priceBigInt.toString()} ${paymentMethod}`
  }

  // AGC: Format date
  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const sharedTopPaddingStyle = { paddingTop: 'calc(env(safe-area-inset-top, 0px) + 72px)' }

  if (!isConnected || !address) {
    return (
      <main className="pb-[88px] px-3 text-white flex flex-col gap-3" style={sharedTopPaddingStyle}>
        <section className="pb-2 border-b border-white/10">
          <div className="flex items-center gap-1.5 mb-2">
            <ShoppingBag className="w-4 h-4 text-purple-300" />
            <p className="text-[9px] uppercase tracking-[0.25em] text-slate-500">PURCHASE HISTORY</p>
          </div>
          <h1 className="text-2xl font-extrabold text-white">Marketplace Purchases</h1>
        </section>
        <div className="text-center text-slate-400 py-8 text-sm">Please connect your wallet to view purchase history</div>
      </main>
    )
  }

  return (
    <main className="pb-[88px] px-3 text-white flex flex-col gap-3" style={sharedTopPaddingStyle}>
      {/* AGC: Header Section - No card, just border divider (following improve_design.md) */}
      <section className="pb-2 border-b border-white/10">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => onNavigateToPage?.('staking')}
              className="p-1.5 hover:bg-slate-900/50 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-4 h-4 text-slate-300" />
            </button>
            <ShoppingBag className="w-4 h-4 text-purple-300" />
            <p className="text-[9px] uppercase tracking-[0.25em] text-slate-500">PURCHASE HISTORY</p>
          </div>
          {/* AGC: FIX 2025-11-28 - Toggle between "My Purchases" and "All Users" */}
          <div className="flex gap-1.5">
            <button
              onClick={() => setViewMode('mine')}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${
                viewMode === 'mine'
                  ? 'bg-purple-600 text-white'
                  : 'bg-slate-900/60 border border-white/5 text-gray-300 hover:bg-slate-800'
              }`}
            >
              Mine
            </button>
            <button
              onClick={() => setViewMode('all')}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${
                viewMode === 'all'
                  ? 'bg-purple-600 text-white'
                  : 'bg-slate-900/60 border border-white/5 text-gray-300 hover:bg-slate-800'
              }`}
            >
              All Users
            </button>
          </div>
        </div>
        <h1 className="text-2xl font-extrabold text-white">
          {viewMode === 'mine' ? 'My Purchases' : 'All Users Purchases'}
        </h1>
      </section>

      {/* AGC: Summary Stats - 2x2 grid like Profile.tsx */}
      {/* AGC: FIX 2025-11-28 - Show combined USDC equivalent total (ETH+OGB converted to USDC) */}
      <section className="grid grid-cols-2 gap-1.5">
        <div className="rounded-xl bg-gradient-to-br from-slate-950 to-slate-900 border border-white/5 px-2 py-2 shadow-[0_8px_20px_rgba(0,0,0,0.45)]">
          <div className="flex items-center gap-1 text-[10px] text-slate-300 uppercase tracking-wide">
            <span>📦</span>
            Items
          </div>
          <div className="text-2xl font-extrabold mt-0.5 text-sky-300">{totals.count}</div>
          <div className="text-[9px] text-slate-500">Total</div>
        </div>
        <div className="rounded-xl bg-gradient-to-br from-slate-950 to-slate-900 border border-white/5 px-2 py-2 shadow-[0_8px_20px_rgba(0,0,0,0.45)]">
          <div className="flex items-center gap-1 text-[10px] text-slate-300 uppercase tracking-wide">
            <span>💰</span>
            Total Spent
          </div>
          <div className="text-2xl font-extrabold mt-0.5 text-emerald-300">
            {filteredPurchases.length > 0 && totals.totalUsdcEquivalent > 0n
              ? formatPrice(totals.totalUsdcEquivalent, 'USDC').split(' ')[0]
              : '0'}
          </div>
          <div className="text-[9px] text-slate-500">
            USDC equivalent
          </div>
        </div>
      </section>

      {/* AGC: FIX 2025-11-28 - Show per-method breakdown */}
      {Object.keys(totals.totalsByMethod).length > 1 && (
        <section className="grid grid-cols-3 gap-1.5">
          {Object.entries(totals.totalsByMethod).map(([method, data]) => (
            <div key={method} className="rounded-lg bg-slate-900/50 border border-white/5 px-2 py-1.5">
              <div className="text-[9px] text-slate-400 uppercase">{method}</div>
              <div className="text-sm font-semibold text-slate-200">
                {formatPrice(data.totalPrice, method).split(' ')[0]}
              </div>
              <div className="text-[8px] text-slate-500">
                ≈ {formatPrice(data.usdcEquivalent, 'USDC')}
              </div>
            </div>
          ))}
        </section>
      )}

      {/* AGC: Filters Section - Compact, no card */}
      <section className="pb-2 border-b border-white/10">
        <div className="flex items-center gap-1 mb-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <p className="text-[9px] uppercase tracking-[0.25em] text-slate-500">FILTER PERIOD</p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {(['today', 'yesterday', 'last7days', 'last30days', 'thisMonth', 'lastMonth', 'total'] as FilterPeriod[]).map((period) => (
            <button
              key={period}
              onClick={() => setFilterPeriod(period)}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${
                filterPeriod === period
                  ? 'bg-purple-600 text-white'
                  : 'bg-slate-900/60 border border-white/5 text-gray-300 hover:bg-slate-800'
              }`}
            >
              {period === 'last7days' ? '7D' : period === 'last30days' ? '30D' : period === 'thisMonth' ? 'This M' : period === 'lastMonth' ? 'Last M' : period.charAt(0).toUpperCase() + period.slice(1)}
            </button>
          ))}
        </div>
      </section>

      {/* AGC: Purchase List - No card, just border divider (following improve_design.md) */}
      <section className="pb-2 border-b border-white/10">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1">
            <Calendar className="w-4 h-4 text-orange-300" />
            <p className="text-[9px] uppercase tracking-[0.25em] text-slate-500">PURCHASES</p>
          </div>
          <span className="text-[9px] text-slate-500">{filteredPurchases.length} items</span>
        </div>

        {loading ? (
          <div className="text-center text-slate-400 py-4 text-[11px]">Loading purchase history...</div>
        ) : error ? (
          <div className="text-center text-red-400 py-4 text-[11px]">Error: {error}</div>
        ) : filteredPurchases.length === 0 ? (
          <div className="text-center text-slate-400 py-4 text-[11px]">No purchases found for the selected period</div>
        ) : (
          <div className="space-y-1 max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-800 scrollbar-track-transparent pr-1">
            {filteredPurchases.map((purchase, index) => (
              <div key={purchase.txHash ? `${purchase.txHash}-${index}` : `purchase-${index}-${purchase.timestamp}`} className="flex items-center justify-between gap-2 py-1.5 border-b border-white/5 last:border-0 hover:bg-slate-900/30 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <p className="text-xs font-semibold text-white truncate">{purchase.itemName}</p>
                    {viewMode === 'mine' && <span className="text-[9px] text-slate-500">×{Number(purchase.amount)}</span>}
                  </div>
                  <p className="text-[10px] text-slate-400">
                    {new Date(purchase.timestamp).toLocaleDateString()} • {viewMode === 'mine' ? formatPricePerItem(purchase.price, purchase.amount, purchase.paymentMethod) : formatPrice(purchase.price, purchase.paymentMethod)}
                  </p>
                  <p className="text-[9px] text-slate-500">{purchase.paymentMethod}</p>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <p className="text-xs font-semibold text-emerald-300">{formatPrice(purchase.price, purchase.paymentMethod)}</p>
                  {/* AGC: FIX 2025-11-28 - Only show transaction link if txHash exists (not available in "All Users" view) */}
                  {purchase.txHash && (
                    <a
                      href={`${BASE_EXPLORER_URL}/tx/${purchase.txHash}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11px] px-3 py-1.5 rounded-lg bg-purple-600/20 border border-purple-400/40 text-purple-300 hover:bg-purple-600/30 hover:text-purple-200 transition-colors font-semibold flex items-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3" />
                      BASE
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </main>
  )
}

